import React, { createContext, useContext, useState, useEffect } from "react";
import { AuthContext } from "../context/AuthContext";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { useHistory } from "react-router-dom";
import { useLoader } from "./LoaderProvider";

// Create the context

const AssessmentContext = createContext();

// Create a provider component

const severityOptions = {
  text: "Please select severity",
  buttons: [
    { severity: 0, name: "None" },
    { severity: 1, name: "Minimal" },
    { severity: 2, name: "Mild" },
    { severity: 3, name: "Moderate" },
    { severity: 4, name: "Severe" },
  ],
};

const yesOrNoOptions = {
  text: "Choose one",
  buttons: [
    { severity: "Yes", name: "Yes" },
    { severity: "No", name: "No" },
  ],
};

const askPatient = "Ask patient";
const askPatientTo = `${askPatient} to`;

export const AssessmentProvider = ({ children }) => {
  const { handleLoader } = useLoader();
  const { makeAuthenticatedApiCall } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const defaultAssessmentObject = {
    bodyAreas: {
      sectionName: "Body Areas",
      key: "bodyAreas",
      sectionAssessment: [
        {
          name: "Facial & Oral",
          key: "facialAndOral",
          whatsThis: "ABC",
          questions: [
            {
              title: "Muscles of facial expression",
              description:
                "e.g., movements of forehead, eyebrows, periorbital areas, cheeks; including frowning, blinking, smiling,  grimacing",
              required: true,
              defaultValue: 0,
            },
            {
              title: "Lips and perioral area",
              description: "e.g., puckering, pouting, smacking",
              required: true,
              defaultValue: 0,
            },
            {
              title: "Jaw",
              description:
                "e.g., biting, clenching, chewing, mouth opening, lateral movement",
              required: true,
              defaultValue: 0,
            },
            {
              title: "Tongue",
              description:
                "Rate only increases in movement both in and out of mouth, NOT ability to sustain movement",
              required: true,
              defaultValue: 0,
            },
          ],
          options: severityOptions,
          walkthrough: [
            {
              text: "Ask the patient whether there is anything in his/her mouth (i.e., gum, candy, etc.) and if there is, to remove it.",
            },
            {
              text: "Evaluate specific areas of the body and rate, on a scale of 0-4, based on the frequency of the movements and how easy they are to detect",
            },
            {
              text: "Ask patient whether he/she notices any movements in mouth, face, hands, or feet. If yes, ask to describe and to what extent they currently bother patient or interfere with his/her activities.",
            },
            {
              text: "Ask the patient to open his or her mouth. Do this twice.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_6_AIMS_Tutorial.mp4",
            },
            {
              text: "Ask the patient to protrude his or her tongue. Do this twice.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_7_AIMS_Tutorial.mp4",
            },
          ],
        },
        {
          name: "Extremity",
          key: "extremity",
          whatsThis: "ABC",
          questions: [
            {
              title: "Upper (arms, wrists, hands, fingers)",
              description:
                "Include choreic movements (i.e., rapid, objectively purposeless, irregular, spontaneous) athetoid movements (i.e., slow, irregular, complex, serpentine). Do NOT include tremor (i.e., repetitive, regular, rhythmic)",
              required: true,
              defaultValue: 0,
            },
            {
              title: "Lower (legs, knees, ankles, toes)",
              description:
                "e.g., lateral knee movement, foot tapping, heel dropping, foot squirming, inversion and eversion of foot",
              required: true,
              defaultValue: 0,
            },
          ],
          options: severityOptions,
          walkthrough: [
            {
              text: "Have the patient sit in a firm chair, that has a straight back and no arms, with hands on knees, legs slightly apart, and feet flat on the floor. Observe the entire body and rate any abnormal movements.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_4_AIMS_Tutorial.mp4",
            },
            {
              text: "Ask patient to sit with hands hanging unsupported. If male, between his legs, if female and wearing a dress, hanging over her knees. Observe hands and other body areas.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_5_AIMS_Tutorial.mp4",
            },
            {
              text: "Ask patient to tap thumb with each finger as rapidly as possible for 10–15 seconds; separately with right hand, then with left hand. Observe facial and leg movements.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_8_AIMS_Tutorial.mp4",
            },
            {
              text: "Flex and extend patient’s left and right arms – one at a time. Note any rigidity or muscle stiffness that could indicate the potential presence of parkinsonism.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_9_AIMS_Tutorial.mp4",
            },
          ],
        },
        {
          name: "Truncal",
          key: "truncal",
          whatsThis: "ABC",
          questions: [
            {
              title: "Neck, shoulders, hips",
              description:
                "e.g., rocking, twisting, squirming, pelvic gyrations",
              required: true,
              defaultValue: 0,
            },
          ],
          options: severityOptions,
          walkthrough: [
            {
              text: "Ask the patient to stand, arms to the side, so they can be observed in profile and straight on. Observe for truncal movements.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_10_AIMS_Tutorial.mp4",
            },
            {
              text: "Ask patient to extend both arms outstretched in front with palms down. Observe trunk, legs, and mouth.",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_11_AIMS_Tutorial.mp4",
            },
            {
              text: "Have patient walk a few paces, turn, and walk back to the chair. (Observe hands and gait.) Do this twice",
              url: "https://resource-devteva.amalnprod.avhana.com/Videos/Step_12_AIMS_Tutorial.mp4",
            },
          ],
        },
      ],
      sectionCompleted: false,
    },
    globalJudgements: {
      sectionName: "Global Judgment",
      key: "globalJudgements",
      sectionAssessment: [
        {
          name: "Global Judgment",
          key: "globalJudgements",
          whatsThis: "ABC",
          questions: [
            {
              title: "Severity of abnormal movements overall",
              description: null,
              required: false,
              defaultValue: 0,
            },
            {
              title: "Incapacitation due to abnormal movements",
              description: null,
              required: false,
              defaultValue: 0,
            },
            {
              title: "Patient's awareness of abnormal movements",
              description:
                "Rate only patient’s report: 0 = No awareness, 1 = Aware, no distress, 2 = Aware, mild distress, 3 = Aware, moderate distress, 4 = Aware, severe distress",
              required: false,
              defaultValue: 0,
            },
          ],
          options: severityOptions,
          walkthrough: [
            {
              text: "For item 8, assess the overall severity of movements based on the highest score for items 1-7 (i.e., if the highest score for any item(s) in 1-7 is a 3, the rating for item 8 should be a 3).",
            },
            {
              text: "For item 9, ask patient whether any movements in their mouth, face, hands, or feet interfere with their regular activities.",
            },
            {
              text: `For item 10, ask patient the extent to which their movements are bothersome:`,
              point: [
                "0 No awareness",
                "1 Aware, no distress",
                "2 Aware, mild distress",
                "3 Aware, moderate distress",
                "4 Aware, severe distress",
              ],
            },
          ],
        },
      ],
      sectionCompleted: true,
    },
    dentalStatus: {
      sectionName: "Dental",
      key: "dentalStatus",
      sectionAssessment: [
        {
          name: "Dental",
          key: "dentalStatus",
          whatsThis: "ABC",
          questions: [
            {
              title: "Current problems with teeth and/or dentures?",
              description: null,
              required: false,
              defaultValue: "No",
            },
            {
              title: "Are dentures usually worn?",
              description: null,
              required: false,
              defaultValue: "No",
            },
          ],
          options: yesOrNoOptions,
          walkthrough: [
            {
              text: "Ask patient about the current condition of his/her teeth. Ask if he/she wears dentures. Do teeth or dentures bother them now?",
            },
          ],
        },
      ],
      sectionCompleted: true,
    },
  };
  const [assessment, setAssessment] = useState(defaultAssessmentObject);
  const checkParticularSection = (section) => {
    let isCompleted = [];
    section.forEach((item) => {
      item.questions.forEach((question) => {
        let answered = question["selectedValue"];
        if (answered !== undefined && answered !== null) {
          isCompleted.push(true);
        } else {
          isCompleted.push(false);
        }
      });
    });
    isCompleted = isCompleted.includes(false);
    return !isCompleted;
  };
  const checkSectionCompleted = () => {
    let keys = Object.keys(assessment);
    let tempAssessment = { ...assessment };
    keys.forEach((key) => {
      let sectionAssessment = tempAssessment[key]["sectionAssessment"];
      tempAssessment[key]["sectionCompleted"] =
        checkParticularSection(sectionAssessment);
    });
    setAssessment(tempAssessment);
  };

  const handleAssessmentToggle = (
    section,
    subSection,
    questionIndex,
    value
  ) => {
    let tempAssessment = { ...assessment };
    tempAssessment[section]["sectionAssessment"][subSection].questions[
      questionIndex
    ][`selectedValue`] = value;
    setAssessment(tempAssessment);
    if (section === "bodyAreas") {
      checkSectionCompleted();
    }
  };

  function validateAndCleanInput(input) {
    // Trim spaces and newlines from the start and end
    const trimmedInput = input.trim();

    // Check if the trimmed input contains at least one letter
    const containsValidCharacters = /[a-zA-Z]/.test(trimmedInput);

    if (containsValidCharacters) {
      // If valid, replace multiple spaces or newlines with a single space
      const cleanedInput = trimmedInput.replace(/[\s\n]+/g, " ");
      return cleanedInput;
    }

    // If invalid, return null or handle accordingly
    return null;
  }

  const handleNotes = (section, subSectionIndex, notes) => {
    let tempAssessment = { ...assessment };
    tempAssessment[section]["sectionAssessment"][subSectionIndex][`notes`] =
      notes;
    setAssessment(tempAssessment);
  };

  const handleExpertNotes = (sectionIndex, subSectionIndex, notes) => {
    const sectionKey = Object.keys(assessment)[sectionIndex];
    handleNotes(sectionKey, subSectionIndex, notes);
  };

  const processAssessmentDataForStoring = (status) => {
    let object = {
      status: status,
      assessmentData: {},
    };
    let sections = Object.keys(assessment);
    sections.forEach((item) => {
      object["assessmentData"][item] = {};
      let subSection = assessment[item]["sectionAssessment"];
      subSection.forEach((item2) => {
        let name = item2.key;
        if (item !== "bodyAreas") {
          object["assessmentData"][name] = {};
        } else {
          object["assessmentData"][item][name] = {};
        }
        let questions = item2.questions;
        let notes = item2.notes;
        let responses = questions.map((question, index) => {
          let answer = question.selectedValue;
          if (answer !== undefined && answer !== null) {
            if (name === "dentalStatus") {
              if (answer && answer === "Yes") {
                answer = true;
              } else if (answer && answer === "No") {
                answer = false;
              } else {
                answer = false;
              }
            }
          } else {
            answer = null;
          }

          return {
            question: index + 1,
            answerOption: answer,
          };
        });
        if (item !== "bodyAreas") {
          object["assessmentData"][name]["responses"] = responses;
          object["assessmentData"][name]["notes"] = notes;
        } else {
          object["assessmentData"][item][name]["responses"] = responses;
          object["assessmentData"][item][name]["notes"] = notes;
        }
      });
    });
    return object;
  };

  const storeAssessmentsData = async (patientId, data) => {
    handleLoader(true);
    let response = await makeAuthenticatedApiCall(
      "post",
      `/api/userservice/saveAssessments/${patientId}`,
      data
    );
    handleLoader(false);
    if (response?.status === 200 && response.data) {
      let assessmentId = response.data.assessmentId;
      history.push({
        pathname: `/patients/${patientId}/assessment/${assessmentId}`,
      });
    }
    return response;
  };
  const updateAssessmentsData = async (patientId, assessmentId, data) => {
    handleLoader(true);

    let response = await makeAuthenticatedApiCall(
      "post",
      `/api/userservice/saveAssessments/${patientId}/${assessmentId}`,
      data
    );
    handleLoader(false);
    return response;
  };

  const getPatientAndAssessmentId = () => {
    let patientId;
    let assessmentId;
    let pathname = location.pathname.substring(1);
    pathname = pathname.split("/");
    patientId = pathname[1];
    if (pathname.length > 3) {
      assessmentId = pathname[3];
    }
    return { patientId, assessmentId };
  };
  const saveAssessmentData = async (status) => {
    let { patientId, assessmentId } = getPatientAndAssessmentId();
    let processedData = processAssessmentDataForStoring(status);
    let response;
    if (assessmentId) {
      response = await updateAssessmentsData(
        patientId,
        assessmentId,
        processedData
      );
    } else {
      response = await storeAssessmentsData(patientId, processedData);
    }
    return response;
  };

  const startNewAssessment = () => {
    setAssessment(defaultAssessmentObject);
  };

  const handleResize = () => {
    const padding = 40;
    const assessmentWidth = 760;
    const screenSize = window.innerWidth;
    let rightPortionSize = screenSize - padding - assessmentWidth;
    rightPortionSize = Math.ceil(rightPortionSize / 2);
    const divSize = rightPortionSize;
    setDrawerWidth(`${divSize}px`);
  };
  const [drawerWidth, setDrawerWidth] = useState();

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <AssessmentContext.Provider
      value={{
        assessment,
        handleAssessmentToggle,
        handleNotes,
        handleExpertNotes,
        saveAssessmentData,
        startNewAssessment,
        drawerWidth,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => useContext(AssessmentContext);
